import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'
import type { ProfileResponse, ProfileBeneficioItem } from '../types/contentful.types'

/**
 * Tipos para los datos procesados del perfil
 */
interface ProfileData {
	buttonSession: string | false
	buttonUpdateData: string | false
}

interface StatusProfile {
	afiliadoSura: ProfileBeneficioItem | false
	noComfama: ProfileBeneficioItem | false
	afiliadoCajaComfama: ProfileBeneficioItem | false
	SuraIpsComfama: ProfileBeneficioItem | false
	suscrito: ProfileBeneficioItem | false
}

interface ProfileContent {
	suscripcionMultitienda: ProfileBeneficioItem | null
}

interface ProcessedProfileData {
	profileData: ProfileData
	statusProfile: StatusProfile
	content: ProfileContent
}

/**
 * Transforma los datos raw de Contentful al formato esperado por los componentes
 */
const transformProfileData = (rawData: ProfileResponse): ProcessedProfileData => {
	if (!rawData?.homeCollection?.items?.[0]?.vistas?.miPerfil) {
		return {
			profileData: {
				buttonSession: false,
				buttonUpdateData: false
			},
			statusProfile: {
				afiliadoSura: false,
				noComfama: false,
				afiliadoCajaComfama: false,
				SuraIpsComfama: false,
				suscrito: false
			},
			content: {
				suscripcionMultitienda: null
			}
		}
	}

	const miPerfil = rawData.homeCollection.items[0].vistas.miPerfil
	const beneficioItems = miPerfil.beneficioCollection?.items || []

	// Función helper para encontrar items por idReto
	const findItemById = (idReto: string): ProfileBeneficioItem | false => {
		const item = beneficioItems.find(item => item.idReto === idReto)
		return item || false
	}

	return {
		profileData: {
			buttonSession: miPerfil.textoBotonSecundario || false,
			buttonUpdateData: miPerfil.textoBoton || false
		},
		statusProfile: {
			afiliadoSura: findItemById('afiliadoSuira'),
			noComfama: findItemById('noComfama'),
			afiliadoCajaComfama: findItemById('afiliadoCajaComfama'),
			SuraIpsComfama: findItemById('Sura+IpsComfama2'),
			suscrito: findItemById('suscrito')
		},
		content: {
			suscripcionMultitienda: findItemById('suscripcionMultitienda') || null
		}
	}
}

/**
 * Hook para obtener y procesar los datos de perfil de Contentful
 * Utiliza Tanstack Query para cache de 5 días y mejores prácticas
 *
 * @returns {Object} Objeto con loading, error y datos procesados
 */
export const useContentfulProfile = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'profile'] as const,
		queryFn: queriesApi.getProfile,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores de manera moderna
	if (error) {
		console.error('Error al obtener datos de Profile desde Contentful:', error)
	}

	// Procesar datos usando memoización para evitar recalculos innecesarios
	const processedData = useMemo(() => {
		if (!rawData || isLoading || error) {
			return {
				profileData: {
					buttonSession: false,
					buttonUpdateData: false
				},
				statusProfile: {
					afiliadoSura: false,
					noComfama: false,
					afiliadoCajaComfama: false,
					SuraIpsComfama: false,
					suscrito: false
				},
				content: {
					suscripcionMultitienda: null
				}
			}
		}

		try {
			return transformProfileData(rawData)
		} catch (transformError) {
			console.error('Error al transformar datos de Profile:', transformError)
			return {
				profileData: {
					buttonSession: false,
					buttonUpdateData: false
				},
				statusProfile: {
					afiliadoSura: false,
					noComfama: false,
					afiliadoCajaComfama: false,
					SuraIpsComfama: false,
					suscrito: false
				},
				content: {
					suscripcionMultitienda: null
				}
			}
		}
	}, [rawData, isLoading, error])

	// Información adicional sobre el estado del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000) // 5 días desde ahora
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (manteniendo compatibilidad con el hook original)
		loading: isLoading,
		error,
		...processedData,

		// Funcionalidades adicionales de Tanstack Query
		refetch,
		isStale,
		isRefetching,
		cacheInfo,

		// Datos raw por si se necesitan
		rawData
	}
}

export default useContentfulProfile
